import { useEffect, useState } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { submitCase, assignCase, transitionCase, listReviewers } from '../services/api';
import { SpecialtySelect } from '../components/SpecialtySelect';
import { BusyButton } from '../components/BusyButton';
import { useConfirm, type ConfirmOptions } from '../components/ConfirmProvider';
import { useToast } from '../components/ToastProvider';
import { errMessage } from '../errors';
import { enumLabel } from '../labels';
import { getLang } from '../language';

type Case = components['schemas']['Case'];
type Reviewer = components['schemas']['Reviewer'];

/**
 * Workflow actions on a case, gated by status and role:
 *  - DRAFT: the local doctor submits — either self-assigning to a reviewer, or sending to triage.
 *  - TRIAGE: an admin assigns a reviewer.
 * After an action succeeds, the parent refreshes the case.
 */
export function CaseActions({
  theCase,
  roles,
  onChanged,
}: {
  theCase: Case;
  roles: string[];
  onChanged: () => void;
}) {
  const [reviewers, setReviewers] = useState<Reviewer[]>([]);
  const [reviewerId, setReviewerId] = useState('');
  // Pre-fill the specialty the local doctor already chose at creation, so submit/assign don't
  // ask for it again. It stays editable in case triage wants to correct it.
  const [specialtyId, setSpecialtyId] = useState(theCase.specialtyId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  const isAdmin = roles.includes('ADMIN') || roles.includes('GLOBAL_ADMIN');
  const isLocal = roles.includes('LOCAL_DOCTOR');
  const isReviewer = roles.includes('REVIEWER');

  const showSubmit = theCase.status === 'DRAFT' && (isLocal || isAdmin);
  const showAssign = theCase.status === 'TRIAGE' && isAdmin;
  // The assigned reviewer (or an admin) explicitly starts the review, so ASSIGNED -> UNDER_REVIEW
  // is a deliberate action rather than a side effect of recording a recommendation. This keeps
  // the status honest ("whose turn") while a case is actually being worked.
  const showStartReview = theCase.status === 'ASSIGNED' && (isReviewer || isAdmin);
  // When the reviewer has asked for more info, the local doctor provides it (via messages/docs)
  // then resumes review, moving the case back to the reviewer.
  const showResume = theCase.status === 'WAITING_FOR_LOCAL_DOCTOR' && (isLocal || isAdmin);
  // After a recommendation, the local doctor acknowledges it and follows up with the patient,
  // then closes the case. (Local doctor or admin.)
  const showFollowUp = theCase.status === 'RECOMMENDATION_PROVIDED' && (isLocal || isAdmin);
  const showClose = theCase.status === 'LOCAL_FOLLOW_UP' && (isLocal || isAdmin);

  useEffect(() => {
    if (showSubmit || showAssign) {
      listReviewers().then((r) => setReviewers(r.items)).catch(() => setReviewers([]));
    }
  }, [showSubmit, showAssign]);

  // Keep the specialty in step with the case (e.g. after a reload), pre-filling the doctor's
  // earlier choice so it need not be re-selected.
  useEffect(() => {
    setSpecialtyId(theCase.specialtyId ?? '');
  }, [theCase.specialtyId]);

  // Always show a short "whose turn / next step" hint so it's clear what happens next, even
  // when the current user has no action to take.
  const hint = (() => {
    switch (theCase.status) {
      case 'DRAFT':
        return 'Draft — the local doctor completes and submits this case.';
      case 'SUBMITTED':
        return 'Submitted — awaiting assignment.';
      case 'TRIAGE':
        return 'In triage — an administrator will assign a reviewer.';
      case 'ASSIGNED':
        return 'Assigned — the reviewer will begin the review.';
      case 'UNDER_REVIEW':
        return 'Under review — the reviewer is reviewing this case.';
      case 'WAITING_FOR_LOCAL_DOCTOR':
        return 'Waiting for the local doctor to provide more information, then resume the review.';
      case 'RECOMMENDATION_PROVIDED':
        return 'A recommendation has been provided — the local doctor follows up with the patient.';
      case 'LOCAL_FOLLOW_UP':
        return 'Local follow-up in progress — close the case when complete.';
      case 'CLOSED':
        return 'This case is closed.';
      default:
        return `Status: ${enumLabel('caseStatus', theCase.status, getLang())}`;
    }
  })();

  if (!showSubmit && !showAssign && !showStartReview && !showResume && !showFollowUp && !showClose) {
    // No action for this user in this state — still show the status hint for clarity.
    return (
      <Card className="shadow-sm mb-3">
        <Card.Body className="text-muted small">{hint}</Card.Body>
      </Card>
    );
  }

  const run = async (
    fn: () => Promise<unknown>,
    opts?: { confirm?: ConfirmOptions; success?: string },
  ) => {
    if (opts?.confirm && !(await confirm(opts.confirm))) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (opts?.success) toast.success(opts.success);
      onChanged();
    } catch (e) {
      const msg = errMessage(e, 'Action failed');
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const reviewerSelect = (
    <Form.Select value={reviewerId} onChange={(e) => setReviewerId(e.target.value)}>
      <option value="">Select a reviewer…</option>
      {reviewers.map((r) => (
        <option key={r.userId} value={r.userId}>{r.name ?? r.email}</option>
      ))}
    </Form.Select>
  );

  const specialtySelect = (
    <SpecialtySelect
      value={specialtyId}
      onChange={setSpecialtyId}
      allowEmpty={false}
      placeholder="Search a specialty…"
    />
  );

  return (
    <Card className="shadow-sm mb-3 border-primary">
      <Card.Header>Actions</Card.Header>
      <Card.Body>
        <p className="text-muted small">{hint}</p>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {showSubmit && (
          <>
            <p className="text-muted">
              Submit this case. Either assign it directly to a reviewer, or send it to triage for
              an administrator to assign.
            </p>
            <Row className="g-2 align-items-end mb-3">
              <Col md={5}>
                <Form.Label className="small text-muted">Reviewer (for direct assign)</Form.Label>
                {reviewerSelect}
              </Col>
              <Col md={5}>
                <Form.Label className="small text-muted">Specialty (required if assigning)</Form.Label>
                {specialtySelect}
              </Col>
            </Row>
            <div className="d-flex gap-2">
              <BusyButton
                variant="success"
                loading={busy}
                busyLabel="Working…"
                disabled={!reviewerId || !specialtyId}
                onClick={() =>
                  run(
                    () =>
                      submitCase(theCase.caseId, {
                        version: theCase.version,
                        assignReviewerId: reviewerId,
                        specialtyId,
                      }),
                    { success: 'Case submitted and assigned.' },
                  )
                }
              >
                Submit &amp; assign reviewer
              </BusyButton>
              <BusyButton
                variant="outline-primary"
                loading={busy}
                busyLabel="Working…"
                onClick={() =>
                  run(
                    () =>
                      submitCase(theCase.caseId, {
                        version: theCase.version,
                        specialtyId: specialtyId || null,
                      }),
                    { success: 'Case submitted to triage.' },
                  )
                }
              >
                Submit to triage
              </BusyButton>
            </div>
          </>
        )}

        {showAssign && (
          <>
            <p className="text-muted">Assign this triaged case to a reviewer.</p>
            <Row className="g-2 align-items-end mb-3">
              <Col md={5}>
                <Form.Label className="small text-muted">Reviewer</Form.Label>
                {reviewerSelect}
              </Col>
              <Col md={5}>
                <Form.Label className="small text-muted">Specialty (optional)</Form.Label>
                {specialtySelect}
              </Col>
            </Row>
            <BusyButton
              variant="success"
              loading={busy}
              busyLabel="Working…"
              disabled={!reviewerId}
              onClick={() =>
                run(
                  () =>
                    assignCase(theCase.caseId, {
                      version: theCase.version,
                      reviewerId,
                      specialtyId: specialtyId || null,
                    }),
                  { success: 'Reviewer assigned.' },
                )
              }
            >
              Assign reviewer
            </BusyButton>
          </>
        )}

        {showStartReview && (
          <>
            <p className="text-muted">
              Begin reviewing this case. This marks it as under review so it's clear the work has
              started.
            </p>
            <BusyButton
              variant="primary"
              loading={busy}
              busyLabel="Working…"
              onClick={() =>
                run(
                  () =>
                    transitionCase(theCase.caseId, {
                      version: theCase.version,
                      toStatus: 'UNDER_REVIEW',
                    }),
                  { success: 'Review started.' },
                )
              }
            >
              Start review
            </BusyButton>
          </>
        )}

        {showResume && (
          <>
            <p className="text-muted">
              The reviewer requested more information. Add it via messages or documents above,
              then resume the review to send it back to the reviewer.
            </p>
            <BusyButton
              variant="primary"
              loading={busy}
              busyLabel="Working…"
              onClick={() =>
                run(
                  () =>
                    transitionCase(theCase.caseId, {
                      version: theCase.version,
                      toStatus: 'UNDER_REVIEW',
                    }),
                  { success: 'Review resumed.' },
                )
              }
            >
              Resume review
            </BusyButton>
          </>
        )}

        {showFollowUp && (
          <>
            <p className="text-muted">
              Acknowledge the recommendation and follow up with the patient, or close the case
              directly if no further local action is needed.
            </p>
            <div className="d-flex gap-2">
              <BusyButton
                variant="primary"
                loading={busy}
                busyLabel="Working…"
                onClick={() =>
                  run(
                    () =>
                      transitionCase(theCase.caseId, {
                        version: theCase.version,
                        toStatus: 'LOCAL_FOLLOW_UP',
                      }),
                    { success: 'Acknowledged — now in local follow-up.' },
                  )
                }
              >
                Acknowledge &amp; follow up
              </BusyButton>
              <BusyButton
                variant="outline-secondary"
                loading={busy}
                busyLabel="Working…"
                onClick={() =>
                  run(
                    () =>
                      transitionCase(theCase.caseId, {
                        version: theCase.version,
                        toStatus: 'CLOSED',
                      }),
                    {
                      confirm: {
                        title: 'Close this case?',
                        body: 'Closing marks the case complete. You can reopen it only via an administrator. Continue?',
                        confirmLabel: 'Close case',
                        variant: 'danger',
                      },
                      success: 'Case closed.',
                    },
                  )
                }
              >
                Close case
              </BusyButton>
            </div>
          </>
        )}

        {showClose && (
          <>
            <p className="text-muted">Close this case once local follow-up is complete.</p>
            <BusyButton
              variant="success"
              loading={busy}
              busyLabel="Working…"
              onClick={() =>
                run(
                  () =>
                    transitionCase(theCase.caseId, {
                      version: theCase.version,
                      toStatus: 'CLOSED',
                    }),
                  {
                    confirm: {
                      title: 'Close this case?',
                      body: 'Closing marks the case complete. You can reopen it only via an administrator. Continue?',
                      confirmLabel: 'Close case',
                      variant: 'danger',
                    },
                    success: 'Case closed.',
                  },
                )
              }
            >
              Close case
            </BusyButton>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
